/**
 * EinzelSimulationTab - Detaillierte Einzelsimulations-Komponente für Portfolio-Strategien
 *
 * Diese React-Komponente bietet eine umfassende Analyse-Oberfläche für deterministische
 * Einzelsimulationen beider Portfolio-Strategien (1-Topf und 3-Topf) mit detaillierten
 * Jahr-für-Jahr Darstellungen und direkten Vergleichsmöglichkeiten.
 *
 * Hauptfunktionen:
 * - Parallele Simulation und Visualisierung beider Portfolio-Strategien
 * - Detaillierte Jahr-für-Jahr Tabellen mit Kapitalentwicklung
 * - Interaktive Chart-Darstellung mit flexibler Skalierung
 * - Steueroptimierungs-Details und Freibetrag-Ausschöpfung
 * - Export-Funktionalität für Simulationsergebnisse
 * - Responsive UI mit AWS Cloudscape Design System
 *
 * Im Gegensatz zu MonteCarloTab zeigt diese Komponente deterministische Einzelläufe
 * mit präzisen Jahr-für-Jahr Details anstatt statistischer Aggregationen.
 *
 * @author Portfolio-3Topf Team
 * @version 2.0.0
 */

import React, { useMemo } from 'react';
import {
  Alert,
  Badge,
  Box,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  LineChart,
  SpaceBetween,
  Table,
  TextContent,
} from '@cloudscape-design/components';
import { ThreeTopfSimulationOptions as SimulationOptions, ThreeTopfYearData as YearData } from '@/types/simulation';
import { OneTopfSimulationOptions, OneTopfYearData, SteuerParameter } from '@/types';
import { GARCHRateCalculationIntegration, GARCHUtils } from '@/utils/garchModeling';
import { TransactionService } from '@/services/TransactionService';
import { getEndOfYearMarketStatus, getStartOfYearMarketStatus } from '@/utils/MarketStatusUtils';

// CSS styles for top-aligned table cells
const tableStyles = `
  .top-aligned-table table th,
  .top-aligned-table table td {
    vertical-align: top !important;
  }
`;

// Inject styles into the document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = tableStyles;
  if (!document.head.querySelector('style[data-table-alignment]')) {
    styleElement.setAttribute('data-table-alignment', 'true');
    document.head.appendChild(styleElement);
  }
}

/**
 * Props für die EinzelSimulationTab-Komponente
 *
 * Diese Schnittstelle definiert alle erforderlichen Daten und Kontrollfunktionen
 * für die deterministische Einzelsimulations-Darstellung.
 */
interface EinzelSimulationTabProps {
  // 3-Topf Simulationsdaten
  /** Jahr-für-Jahr Daten der 3-Topf-Simulation mit detaillierter Topf-Struktur */
  simulationData: YearData[];

  /** Steuerparameter für die Simulation */
  steuerParameter: SteuerParameter | null;

  /** Simulationsoptionen für 3-Topf-Strategie */
  simulationOptions?: SimulationOptions;

  // 1-Topf Simulationsdaten
  /** Jahr-für-Jahr Daten der 1-Topf-Simulation mit vereinfachter Struktur */
  oneTopfSimulationData: OneTopfYearData[];

  /** Simulationsoptionen für 1-Topf-Strategie */
  oneTopfSimulationOptions?: OneTopfSimulationOptions;

  // Krisendefinition
  /** Krisendefinition für Simulationen */
  crisisDefinition: string;

  // Chart-Steuerung
  /** Chart-Steuerung entfernt - logarithmische Skala nicht mehr verfügbar */
}

/**
 * EinzelSimulationTab - Hauptkomponente für deterministische Portfolio-Simulationen
 *
 * Diese Komponente rendert detaillierte Einzelsimulationen mit Jahr-für-Jahr-Analyse
 * und direktem Vergleich zwischen 1-Topf und 3-Topf Strategien.
 */
export const EinzelSimulationTab: React.FC<EinzelSimulationTabProps> = ({
  simulationData,
  oneTopfSimulationData,
  steuerParameter: _steuerParameter,
  simulationOptions,
  oneTopfSimulationOptions: _oneTopfSimulationOptions,
  crisisDefinition: _crisisDefinition,
}) => {
  // GARCH detection for both strategies
  const isThreeTopfUsingGARCH = simulationOptions ? GARCHUtils.isGARCHOption(simulationOptions.investmentReturnOption) : false;
  const isOneTopfUsingGARCH = _oneTopfSimulationOptions ? GARCHUtils.isGARCHOption(_oneTopfSimulationOptions.investmentReturnOption) : false;
  const isAnyStrategyUsingGARCH = isThreeTopfUsingGARCH || isOneTopfUsingGARCH;

  // GARCH analysis data (cached for performance)
  const garchAnalysis = useMemo(() => {
    if (!isAnyStrategyUsingGARCH) {return null;}

    try {
      return GARCHRateCalculationIntegration.analyzeIndexDataForGARCH('world');
    } catch (error) {
      // Failed to load GARCH analysis
      return null;
    }
  }, [isAnyStrategyUsingGARCH]);

  // Helper function to determine if GARCH is being used
  const isUsingGARCH =
    simulationOptions?.investmentReturnOption &&
    GARCHUtils.isGARCHOption(simulationOptions.investmentReturnOption);
  

  // Helper function to calculate volatility regime
  const getVolatilityRegime = (returnValue: number): { regime: string; color: 'red' | 'blue' | 'green' } => {
    const absReturn = Math.abs(returnValue);
    if (absReturn > 20) {return { regime: 'Hoch', color: 'red' };}
    if (absReturn > 10) {return { regime: 'Mittel', color: 'blue' };}
    return { regime: 'Niedrig', color: 'green' };
  };

  // Helper function to estimate current volatility from GARCH (simplified approximation)
  const estimateCurrentVolatility = (_item: YearData, index: number): number => {
    // Simple volatility estimation based on recent returns
    if (index < 2) {return 16.0;} // Default market volatility

    const recentReturns = simulationData
      .slice(Math.max(0, index - 2), index + 1)
      .map(d => d.investitionstopfRendite);

    const mean = recentReturns.reduce((sum, r) => sum + r, 0) / recentReturns.length;
    const variance =
      recentReturns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / recentReturns.length;
    return Math.sqrt(variance);
  };

  // Portfolio chart data preparation - SIMPLIFIED back to portfolio values only
  const portfolioChartSeries = useMemo(() => {
    const series = [];

    // 3-Topf strategy series
    if (simulationData && simulationData.length > 0) {
      series.push({
        title: '3-Topf Gesamtvermögen',
        type: 'line' as const,
        data: simulationData.map((item) => ({
          x: item.alter,
          y: item.gesamtsaldo,
        })),
        color: '#1f77b4', // Blue for 3-Topf
      });
    }

    // 1-Topf strategy series
    if (oneTopfSimulationData && oneTopfSimulationData.length > 0) {
      series.push({
        title: '1-Topf Gesamtvermögen',
        type: 'line' as const,
        data: oneTopfSimulationData.map((item) => ({
          x: item.alter,
          y: item.gesamtsaldo,
        })),
        color: '#2ca02c', // Green for 1-Topf
      });
    }

    // Add vertical threshold lines for crisis years (01.01 in crisis)
    if (simulationData && simulationData.length > 0) {
      const crisisYears: number[] = [];
      simulationData.forEach((year, index) => {
        const prevYear = index > 0 ? simulationData[index - 1] : undefined;
        const startOfYearStatus = getStartOfYearMarketStatus(
          year,
          prevYear,
          simulationData,
          _crisisDefinition as 'negative-return' | 'kapitalindex-drawdown'
        );
        if (startOfYearStatus === 'Krise') {
          crisisYears.push(year.alter);
        }
      });

      // Add threshold line for each crisis year
      crisisYears.forEach(age => {
        series.push({
          title: `Krise (Alter ${age})`,
          type: 'threshold' as const,
          x: age,
          color: '#d13212', // Red color for crisis marker
        });
      });
    }

    return series;
  }, [simulationData, oneTopfSimulationData, _crisisDefinition]);

  // Kapitalindex chart data preparation - SEPARATE chart for performance indices
  const kapitalindexChartSeries = useMemo(() => {
    const series = [];

    // 3-Topf Kapitalindex series
    if (simulationData && simulationData.length > 0) {
      series.push({
        title: '3-Topf Kapitalindex',
        type: 'line' as const,
        data: simulationData.map((item, index) => {
          // Use the actual 01.01 value (start of year) - same logic as table
          let startIndex = 100; // Default for first year
          if (index > 0) {
            const prevYear = simulationData[index - 1];
            if (prevYear?.kapitalIndex !== undefined) {
              startIndex = prevYear.kapitalIndex; // Use 31.12 of previous year as 01.01 of current year
            } else {
              // Fallback calculation
              startIndex = 100;
              for (let i = 0; i < index; i++) {
                const yearReturn = simulationData[i].investitionstopfRendite / 100;
                startIndex *= 1 + yearReturn;
              }
            }
          }
          
          return {
            x: item.alter,
            y: Math.round(startIndex),
          };
        }),
        color: '#ff7f0e', // Orange for 3-Topf Kapitalindex
      });
    }

    // 1-Topf Kapitalindex series
    if (oneTopfSimulationData && oneTopfSimulationData.length > 0) {
      series.push({
        title: '1-Topf Kapitalindex',
        type: 'line' as const,
        data: oneTopfSimulationData.map((item, index) => {
          // Use the actual 01.01 value (start of year) - same logic as table
          let startIndex = 100; // Default for first year
          if (index > 0) {
            const prevYear = oneTopfSimulationData[index - 1];
            if (prevYear?.kapitalIndex !== undefined) {
              startIndex = prevYear.kapitalIndex; // Use 31.12 of previous year as 01.01 of current year
            } else {
              // Fallback calculation
              startIndex = 100;
              for (let i = 0; i < index; i++) {
                const yearReturn = oneTopfSimulationData[i].investitionstopfRendite / 100;
                startIndex *= 1 + yearReturn;
              }
            }
          }
          
          return {
            x: item.alter,
            y: Math.round(startIndex),
          };
        }),
        color: '#d62728', // Red for 1-Topf Kapitalindex
      });
    }

    // Add vertical threshold lines for crisis years (01.01 in crisis)
    // We need to calculate crisis years here to include them in the series
    if (simulationData && simulationData.length > 0) {
      const crisisYears: number[] = [];
      simulationData.forEach((year, index) => {
        const prevYear = index > 0 ? simulationData[index - 1] : undefined;
        const startOfYearStatus = getStartOfYearMarketStatus(
          year,
          prevYear,
          simulationData,
          _crisisDefinition as 'negative-return' | 'kapitalindex-drawdown'
        );
        if (startOfYearStatus === 'Krise') {
          crisisYears.push(year.alter);
        }
      });

      // Add threshold line for each crisis year
      crisisYears.forEach(age => {
        series.push({
          title: `Krise (Alter ${age})`,
          type: 'threshold' as const,
          x: age,
          color: '#d13212', // Red color for crisis marker
        });
      });
    }

    return series;
  }, [simulationData, oneTopfSimulationData, _crisisDefinition]);

  // Crisis years for vertical markers in Kapitalindex chart (only when 01.01 is in crisis)
  const crisisYearsAtStart = useMemo(() => {
    const crisisYears: number[] = [];
    
    // Collect years where 01.01 (start of year) is in crisis
    if (simulationData && simulationData.length > 0) {
      simulationData.forEach((year, index) => {
        // Get start-of-year market status using the utility function
        const prevYear = index > 0 ? simulationData[index - 1] : undefined;
        const startOfYearStatus = getStartOfYearMarketStatus(
          year,
          prevYear,
          simulationData,
          _crisisDefinition as 'negative-return' | 'kapitalindex-drawdown'
        );
        
        // Mark this year if 01.01 is in crisis
        if (startOfYearStatus === 'Krise') {
          crisisYears.push(year.alter);
        }
      });
    }
    
    return crisisYears;
  }, [simulationData, _crisisDefinition]);

  // Combined KPI calculations - MATCHING GITHUB ORIGINAL STRUCTURE
  const combinedKPIs = useMemo(() => {
    const kpis = {
      threeTopf: {
        hasData: simulationData && simulationData.length > 0,
        totalYears: simulationData ? simulationData.length : 0,
        successful: false,
        finalBalance: 0,
        totalWithdrawals: 0,
        avgReturn: 0,
        crisisYears: 0,
      },
      oneTopf: {
        hasData: oneTopfSimulationData && oneTopfSimulationData.length > 0,
        totalYears: oneTopfSimulationData ? oneTopfSimulationData.length : 0,
        successful: false,
        finalBalance: 0,
        totalWithdrawals: 0,
        avgReturn: 0,
        crisisYears: 0,
      },
    };

    // 3-Topf KPIs
    if (kpis.threeTopf.hasData) {
      const finalYear = simulationData[simulationData.length - 1];
      kpis.threeTopf.successful = finalYear.gesamtsaldo > 0;
      kpis.threeTopf.finalBalance = finalYear.gesamtsaldo;
      kpis.threeTopf.totalWithdrawals = simulationData.reduce((sum, year) => sum + year.gesamtentnahme, 0);
      kpis.threeTopf.avgReturn = simulationData.reduce((sum, year) => sum + year.investitionstopfRendite, 0) / simulationData.length;
      kpis.threeTopf.crisisYears = simulationData.filter(year => year.istKrise).length;
    }

    // 1-Topf KPIs
    if (kpis.oneTopf.hasData) {
      const finalYear = oneTopfSimulationData[oneTopfSimulationData.length - 1];
      kpis.oneTopf.successful = finalYear.investitionstopfSaldo > 0;
      kpis.oneTopf.finalBalance = finalYear.investitionstopfSaldo;
      kpis.oneTopf.totalWithdrawals = oneTopfSimulationData.reduce((sum, year) => sum + year.gesamtentnahme, 0);
      kpis.oneTopf.avgReturn = oneTopfSimulationData.reduce((sum, year) => sum + year.investitionstopfRendite, 0) / oneTopfSimulationData.length;
      kpis.oneTopf.crisisYears = oneTopfSimulationData.filter(year => year.istKrise).length;
    }

    return kpis;
  }, [simulationData, oneTopfSimulationData]);

  // Helper function to get detailed change breakdown with money flows using TransactionService
  const getDetailedChangeIndicator = (
    potType: 'verbrauch' | 'zins' | 'investition',
    _currentValue: number,
    _previousValue: number | null,
    currentYear: YearData,
    previousYear: YearData | null
  ) => {
    const isFirstYear = currentYear.jahr === simulationData[0]?.jahr;

    // Use TransactionService for consistent transaction calculation
    let transactionData;
      switch (potType) {
        case 'verbrauch':
        transactionData = TransactionService.calculateVerbrauchstopfTransactions(
          currentYear,
          previousYear,
          isFirstYear
        );
          break;
        case 'zins':
        transactionData = TransactionService.calculateZinstopfTransactions(
          currentYear,
          previousYear,
          isFirstYear
        );
          break;
        case 'investition':
        transactionData = TransactionService.calculateInvestitionstopfTransactions(
          currentYear,
          previousYear,
          isFirstYear
        );
          break;
      }
    
    // Convert transaction strings to React components
    const components: React.JSX.Element[] = [];
    
    // Process all transactions (include start balance and all other transactions)
    const transactions = transactionData.transactions;
    
    for (let i = 0; i < transactions.length; i++) {
      const transaction = transactions[i];
      let color = 'text-body-secondary';
      
      // Determine color based on transaction type
      if (transaction.includes('01.01.:') || transaction.includes('31.12.:')) {
        // Start and end balance - Black
        color = 'text-body-secondary';
      } else if (transaction.includes('- (Entnahme)') || transaction.includes('- (in ') || transaction.includes('- (Überschuss an ')) {
        // Withdrawals, outflows, and overflow transfers OUT - Red
        color = 'text-status-error';
      } else if (transaction.includes('+ (aus ') || transaction.includes('+ (Überschuss aus ')) {
        // Inflows and overflow transfers IN - Green
        color = 'text-status-success';
      } else if (transaction.includes('(Rendite)')) {
        // Returns - Red for negative, Green for positive
        color = transaction.includes('+') ? 'text-status-success' : 'text-status-error';
      }

          components.push(
        <Box key={`transaction-${i}`} color={color as any} fontSize="body-s">
          {transaction}
            </Box>
          );
        }

    return (
      <SpaceBetween direction="vertical" size="xxxs">
        {components}
      </SpaceBetween>
    );
  };

  // Helper function for 1-Topf detailed change breakdown
  const getOneTopfDetailedChangeIndicator = (
    currentValue: number,
    previousValue: number | null,
    currentYear: OneTopfYearData,
    previousYear: OneTopfYearData | null
  ) => {
    const isFirstYear = previousValue === null || !previousYear;
    const components = [];

    // 01.01 Startvalue in Black - always show for consistency with 3-Topf
    const startBalance = isFirstYear 
      ? (currentValue / (1 + currentYear.investitionstopfRendite / 100)) // Calculate original balance before interest
      : previousValue;
    components.push(
      <Box key="start" color="inherit" fontSize="body-s">
        01.01.: {Math.round(startBalance).toLocaleString('de-DE')} €
      </Box>
    );

    if (!isFirstYear) {
      // Calculate interest earned from investment returns
      const interestRate = currentYear.investitionstopfRendite / 100;
      const interestEarned = previousValue * interestRate;
      const netChange = currentValue - previousValue;

      // Calculate withdrawal amount
      const cashFlow = netChange - interestEarned;

      // - (Entnahme) value in Red
      if (Math.abs(cashFlow) > 0.01) {
        components.push(
          <Box
            key="cashflow"
            color="text-status-error"
            fontSize="body-s"
          >
            - (Entnahme) {Math.abs(Math.round(cashFlow)).toLocaleString('de-DE')} €
          </Box>
        );
      }

      // +/- (Rendite) value in Red or Green
      if (Math.abs(interestEarned) >= 0.01) {
        const isNegative = interestEarned < 0;
        const displayColor = isNegative ? 'text-status-error' : 'text-status-success';
        const signPrefix = isNegative ? '-' : '+';

        components.push(
          <Box key="interest" color={displayColor} fontSize="body-s">
            {signPrefix} (Rendite) {Math.abs(Math.round(interestEarned)).toLocaleString('de-DE')} €
          </Box>
        );
      } else if (interestEarned === 0) {
        // Explicitly show 0 interest for clarity
        components.push(
          <Box key="interest" color="text-status-success" fontSize="body-s">
            + (Rendite) 0 €
          </Box>
        );
      }
    } else {
      // First year: only show interest/return
      const interestRate = currentYear.investitionstopfRendite / 100;
      const interestEarned = startBalance * interestRate;

      if (Math.abs(interestEarned) >= 0.01) {
        const isNegative = interestEarned < 0;
        const displayColor = isNegative ? 'text-status-error' : 'text-status-success';
        const signPrefix = isNegative ? '-' : '+';

        components.push(
          <Box key="interest" color={displayColor} fontSize="body-s">
            {signPrefix} (Rendite) {Math.abs(Math.round(interestEarned)).toLocaleString('de-DE')} €
          </Box>
        );
      }
    }

    // 31.12. Endvalue in Black
    components.push(
      <Box key="end" color="inherit" fontSize="body-s">
        31.12.: {Math.round(currentValue).toLocaleString('de-DE')} €
      </Box>
    );

    return <div>{components}</div>;
  };


  // 3-Topf Table column definitions - FULLY RESTORED FROM GITHUB ORIGINAL
  const threeTopfTableColumns = [
    {
      id: 'jahr',
      header: 'Alter',
      cell: (item: YearData) => item.alter,
      width: 60,
    },
    {
      id: 'gesamtentnahme',
      header: 'Entnahme',
      cell: (item: YearData) => (
        <Box textAlign="right">
          {`${Math.round(item.gesamtentnahme).toLocaleString('de-DE')} €`}
        </Box>
      ),
      width: 120,
    },
    {
      id: 'inflationsrate',
      header: 'Inflation',
      cell: (item: YearData) => <Box textAlign="right">{`${item.inflationsrate.toFixed(1)}%`}</Box>,
      width: 80,
    },
    {
      id: 'verbrauchstopfSaldo',
      header: 'Verbrauchstopf',
      cell: (item: YearData) => {
        const previousItem = simulationData.find(d => d.jahr === item.jahr - 1);
        const previousValue = previousItem?.verbrauchstopfSaldo || null;

        return (
          <Box textAlign="right">
            {getDetailedChangeIndicator(
              'verbrauch',
              item.verbrauchstopfSaldo,
              previousValue,
              item,
              previousItem || null
            )}
          </Box>
        );
      },
      width: 200,
    },
    {
      id: 'verbrauchstopfZinssatz',
      header: 'Rendite V.',
      cell: (item: YearData) => (
        <Box textAlign="right">{`${item.verbrauchstopfZinssatz.toFixed(1)}%`}</Box>
      ),
      width: 80,
    },
    {
      id: 'zinstopfSaldo',
      header: 'Zinstopf',
      cell: (item: YearData) => {
        const previousItem = simulationData.find(d => d.jahr === item.jahr - 1);
        const previousValue = previousItem?.zinstopfSaldo || null;

        return (
          <Box textAlign="right">
            {getDetailedChangeIndicator(
              'zins',
              item.zinstopfSaldo,
              previousValue,
              item,
              previousItem || null
            )}
          </Box>
        );
      },
      width: 180,
    },
    {
      id: 'zinstopfZinssatz',
      header: 'Rendite Z.',
      cell: (item: YearData) => (
        <Box textAlign="right">{`${item.zinstopfZinssatz.toFixed(1)}%`}</Box>
      ),
      width: 80,
    },
    {
      id: 'investitionstopfSaldo',
      header: 'Investitionstopf',
      cell: (item: YearData) => {
        const previousItem = simulationData.find(d => d.jahr === item.jahr - 1);
        const previousValue = previousItem?.investitionstopfSaldo || null;

        return (
          <Box textAlign="right">
            {getDetailedChangeIndicator(
              'investition',
              item.investitionstopfSaldo,
              previousValue,
              item,
              previousItem || null
            )}
          </Box>
        );
      },
      width: 200,
    },
    {
      id: 'investitionstopfRendite',
      header: 'Rendite I.',
      cell: (item: YearData) => (
        <Box textAlign="right">{`${item.investitionstopfRendite.toFixed(1)}%`}</Box>
      ),
      width: 80,
    },
    {
      id: 'kapitalIndex',
      header: 'Kapitalindex',
      cell: (item: YearData) => {
        // Use actual capital index from simulation data (supports both crisis modes)
        const currentIndex = simulationData.findIndex(d => d.jahr === item.jahr);
        
        // For start index: use previous year's kapitalIndex or calculate if missing
        let startIndex = 100; // Default for first year
        if (currentIndex > 0) {
          const prevYear = simulationData[currentIndex - 1];
          if (prevYear?.kapitalIndex !== undefined) {
            startIndex = prevYear.kapitalIndex;
          } else {
            // Fallback calculation
            startIndex = 100;
            for (let i = 0; i < currentIndex; i++) {
              const yearReturn = simulationData[i].investitionstopfRendite / 100;
              startIndex *= 1 + yearReturn;
            }
          }
        }

        // For end index: use current item's kapitalIndex or calculate if missing
        let endIndex;
        if (item.kapitalIndex !== undefined) {
          endIndex = item.kapitalIndex;
        } else {
          // Fallback calculation
          endIndex = startIndex * (1 + item.investitionstopfRendite / 100);
        }

        return (
          <Box textAlign="right" fontSize="body-s">
            <div>01.01: {Math.round(startIndex).toLocaleString('de-DE')}</div>
            <div>31.12: {Math.round(endIndex).toLocaleString('de-DE')}</div>
          </Box>
        );
      },
      width: 120,
    },
    {
      id: 'gesamtsaldo',
      header: 'Gesamtsaldo',
      cell: (item: YearData) => (
        <Box textAlign="right">{`${Math.round(item.gesamtsaldo).toLocaleString('de-DE')} €`}</Box>
      ),
      width: 140,
    },
    {
      id: 'marktSituation',
      header: 'Marktlage (Jahresende)',
      cell: (item: YearData) => {
        // Find current year index and previous year data
        const currentIndex = simulationData.findIndex(d => d.jahr === item.jahr);
        const previousYear = currentIndex > 0 ? simulationData[currentIndex - 1] : undefined;

        // Get start-of-year and end-of-year market status
        // Hinweis: Diese Spalte zeigt das JAHRESEND-Ergebnis, während die Topf-Aktionen
        // der JAHRESANFANGS-Entscheidung folgen (bewusst, kein Bug).
        const startOfYearStatus = getStartOfYearMarketStatus(
          item,
          previousYear,
          simulationData,
          _crisisDefinition as 'negative-return' | 'kapitalindex-drawdown'
        );
        const endOfYearStatus = getEndOfYearMarketStatus(item);
        
        return (
          <Box textAlign="center" fontSize="body-s">
            <div style={{ marginBottom: '2px' }}>
              01.01: <Badge color={startOfYearStatus === 'Krise' ? 'red' : 'green'} >
                {startOfYearStatus}
              </Badge>
            </div>
            <div>
              31.12: <Badge color={endOfYearStatus === 'Krise' ? 'red' : 'green'}>
                {endOfYearStatus}
              </Badge>
            </div>
          </Box>
        );
      },
      width: 120,
    },
    {
      id: 'auffuellAktion',
      header: 'Info',
      cell: (item: YearData) => (
        <div style={{ 
          wordWrap: 'break-word', 
          lineHeight: '1.2', 
          fontSize: '0.875rem',
          textAlign: 'left',
          whiteSpace: 'pre-line'
        }}>
          {item.auffuellAktion}
        </div>
      ),
      minWidth: 550,
    },
    // GARCH-specific columns (conditionally included)
    ...(isUsingGARCH
      ? [
          {
            id: 'volatilityEstimate',
            header: 'Volatilität',
            cell: (item: YearData) => {
              const index = simulationData.findIndex(d => d.jahr === item.jahr);
              const volatility = estimateCurrentVolatility(item, index);
              return <Box textAlign="right">{`${volatility.toFixed(1)}%`}</Box>;
            },
            width: 90,
          },
          {
            id: 'volatilityRegime',
            header: 'Vol. Regime',
            cell: (item: YearData) => {
              const { regime, color } = getVolatilityRegime(item.investitionstopfRendite);
              return <Badge color={color}>{regime}</Badge>;
            },
            width: 90,
          },
          {
            id: 'garchInfo',
            header: 'GARCH Info',
            cell: (item: YearData) => {
              const absReturn = Math.abs(item.investitionstopfRendite);
              const isVolatilityCluster = absReturn > 15;
              const isLeverageEffect = item.investitionstopfRendite < -10;

              const badges = [];
              if (isVolatilityCluster) {badges.push('Vol-Cluster');}
              if (isLeverageEffect) {badges.push('Leverage');}

              return (
                <SpaceBetween direction="horizontal" size="xs">
                  {badges.map((badge, index) => (
                    <Badge key={index} color="grey">
                      {badge}
                    </Badge>
                  ))}
                  {badges.length === 0 && (
                    <span style={{ color: '#5f6b7a', fontSize: '12px' }}>Normal</span>
                  )}
                </SpaceBetween>
              );
            },
            width: 120,
          },
        ]
      : []),
  ];

  // 1-Topf Table column definitions - FULLY RESTORED FROM GITHUB ORIGINAL
  const oneTopfTableColumns = [
    {
      id: 'jahr',
      header: 'Alter',
      cell: (item: OneTopfYearData) => item.alter,
      width: 60,
    },
    {
      id: 'gesamtentnahme',
      header: 'Entnahme',
      cell: (item: OneTopfYearData) => (
        <Box textAlign="right">
          {`${Math.round(item.gesamtentnahme).toLocaleString('de-DE')} €`}
        </Box>
      ),
      width: 120,
    },
    {
      id: 'inflationsrate',
      header: 'Inflation',
      cell: (item: OneTopfYearData) => (
        <Box textAlign="right">{`${item.inflationsrate.toFixed(1)}%`}</Box>
      ),
      width: 80,
    },
    {
      id: 'investitionstopfSaldo',
      header: 'Investitionstopf',
      cell: (item: OneTopfYearData) => {
        const previousItem = oneTopfSimulationData.find(d => d.jahr === item.jahr - 1);
        const previousValue = previousItem?.investitionstopfSaldo || null;

        return (
          <Box textAlign="right">
            {getOneTopfDetailedChangeIndicator(
              item.investitionstopfSaldo,
              previousValue,
              item,
              previousItem || null
            )}
          </Box>
        );
      },
      width: 200,
    },
    {
      id: 'investitionstopfRendite',
      header: 'Rendite',
      cell: (item: OneTopfYearData) => (
        <Box textAlign="right">{`${item.investitionstopfRendite.toFixed(1)}%`}</Box>
      ),
      width: 80,
    },
    {
      id: 'kapitalIndex1Topf',
      header: 'Kapitalindex',
      cell: (item: OneTopfYearData) => {
        // Use actual capital index from simulation data (supports both crisis modes)
        const currentIndex = oneTopfSimulationData.findIndex(d => d.jahr === item.jahr);
        
        // For start index: use previous year's kapitalIndex or calculate if missing
        let startIndex = 100; // Default for first year
        if (currentIndex > 0) {
          const prevYear = oneTopfSimulationData[currentIndex - 1];
          if (prevYear?.kapitalIndex !== undefined) {
            startIndex = prevYear.kapitalIndex;
          } else {
            // Fallback calculation
            startIndex = 100;
            for (let i = 0; i < currentIndex; i++) {
              const yearReturn = oneTopfSimulationData[i].investitionstopfRendite / 100;
              startIndex *= 1 + yearReturn;
            }
          }
        }

        // For end index: use current item's kapitalIndex or calculate if missing
        let endIndex;
        if (item.kapitalIndex !== undefined) {
          endIndex = item.kapitalIndex;
        } else {
          // Fallback calculation
          endIndex = startIndex * (1 + item.investitionstopfRendite / 100);
        }

        return (
          <Box textAlign="right" fontSize="body-s">
            <div>01.01: {Math.round(startIndex).toLocaleString('de-DE')}</div>
            <div>31.12: {Math.round(endIndex).toLocaleString('de-DE')}</div>
          </Box>
        );
      },
      width: 100,
    },
    {
      id: 'marktSituation',
      header: 'Marktlage (Jahresende)',
      cell: (item: OneTopfYearData) => {
        // Find current year index and previous year data
        const currentIndex = oneTopfSimulationData?.findIndex(d => d.jahr === item.jahr) ?? -1;
        const previousYear = currentIndex > 0 ? oneTopfSimulationData?.[currentIndex - 1] : undefined;

        // Get start-of-year and end-of-year market status
        // Hinweis: Diese Spalte zeigt das JAHRESEND-Ergebnis, während die Topf-Aktionen
        // der JAHRESANFANGS-Entscheidung folgen (bewusst, kein Bug).
        const startOfYearStatus = getStartOfYearMarketStatus(
          item,
          previousYear,
          oneTopfSimulationData || [],
          _crisisDefinition as 'negative-return' | 'kapitalindex-drawdown'
        );
        const endOfYearStatus = getEndOfYearMarketStatus(item);
        
        return (
          <Box textAlign="center" fontSize="body-s">
            <div style={{ marginBottom: '2px' }}>
              01.01: <Badge color={startOfYearStatus === 'Krise' ? 'red' : 'green'} >
                {startOfYearStatus}
              </Badge>
            </div>
            <div>
              31.12: <Badge color={endOfYearStatus === 'Krise' ? 'red' : 'green'}>
                {endOfYearStatus}
              </Badge>
            </div>
          </Box>
        );
      },
      width: 140,
    },
    {
      id: 'entnahmeAktion',
      header: 'Info',
      cell: (item: OneTopfYearData) => (
        <div style={{ wordWrap: 'break-word', lineHeight: '1.2', fontSize: '0.875rem' }}>
          {item.entnahmeAktion}
        </div>
      ),
      width: 350,
    },
    // GARCH-specific columns for 1-Topf (conditionally included)
    ...(_oneTopfSimulationOptions?.investmentReturnOption &&
    GARCHUtils.isGARCHOption(_oneTopfSimulationOptions.investmentReturnOption)
      ? [
          {
            id: 'volatilityEstimate1Topf',
            header: 'Volatilität',
            cell: (item: OneTopfYearData) => {
              const index = oneTopfSimulationData.findIndex(d => d.jahr === item.jahr);
              const recentReturns = oneTopfSimulationData
                .slice(Math.max(0, index - 2), index + 1)
                .map(d => d.investitionstopfRendite);

              const mean = recentReturns.reduce((sum, r) => sum + r, 0) / recentReturns.length;
              const variance =
                recentReturns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) /
                recentReturns.length;
              const volatility = Math.sqrt(variance);

              return <Box textAlign="right">{`${volatility.toFixed(1)}%`}</Box>;
            },
            width: 90,
          },
          {
            id: 'volatilityRegime1Topf',
            header: 'Vol. Regime',
            cell: (item: OneTopfYearData) => {
              const { regime, color } = getVolatilityRegime(item.investitionstopfRendite);
              return <Badge color={color}>{regime}</Badge>;
            },
            width: 90,
          },
          {
            id: 'garchInfo1Topf',
            header: 'GARCH Info',
            cell: (item: OneTopfYearData) => {
              const absReturn = Math.abs(item.investitionstopfRendite);
              const isVolatilityCluster = absReturn > 15;
              const isLeverageEffect = item.investitionstopfRendite < -10;

              const badges = [];
              if (isVolatilityCluster) {badges.push('Vol-Cluster');}
              if (isLeverageEffect) {badges.push('Leverage');}

              return (
                <SpaceBetween direction="horizontal" size="xs">
                  {badges.map((badge, index) => (
                    <Badge key={index} color="grey">
                      {badge}
                    </Badge>
                  ))}
                  {badges.length === 0 && (
                    <span style={{ color: '#5f6b7a', fontSize: '12px' }}>Normal</span>
                  )}
                </SpaceBetween>
              );
            },
            width: 120,
          },
        ]
      : []),
  ];

  // Create condensed simulation summary content in German with parameters first - FULL GITHUB ORIGINAL
  const simulationSummary = useMemo(() => {
    if (!_steuerParameter) {return '';}

    const retirementAge = _steuerParameter.retirementAge || 0;
    const berechnungBisLebensalter = _steuerParameter.berechnungBisLebensalter || 0;
    const yearsFunded = berechnungBisLebensalter - retirementAge;
    const startingCapital = _steuerParameter.vorhandenesKapital || 0;
    const annualWithdrawal =
      (startingCapital * (_steuerParameter.portfolioEntnahmeRate || 3.0)) / 100;

    const inflationText = (() => {
      switch (simulationOptions?.inflationOption) {
        case 'keine-inflation':
          return 'Keine Inflation (0%)';
        case 'parameter-inflation':
          return `Inflation mit Prozentwert aus Eingabeparameter (${_steuerParameter?.inflationsrate || 2.0}%)`;
        case 'historische-inflation':
          return 'Inflation als Zufallswert aus historische Inflation';
        case 'statistisch-inflation':
          return 'Inflation als Zufallswert basierend auf Mittelwert und Standardabweichung';
        default:
          return 'Keine Inflation (0%)';
      }
    })();

    const investmentReturnText = (() => {
      switch (simulationOptions?.investmentReturnOption) {
        case 'keine-rendite':
          return 'Keine Rendite (0%)';
        case 'parameter-rendite':
          return `Normalverteilte Rendite um Parameterwert (Mittelwert: ${_steuerParameter?.renditeInvestitionstopf || 7.0}%, Volatilität: ~16%)`;
        case 'konstante-rendite':
          return `Konstante Rendite aus Parameterwert (${_steuerParameter?.renditeInvestitionstopf || 7.0}%)`;
        case 'historische-rendite':
          return 'Rendite als Zufallswert aus MSCI World historischen Renditen';
        case 'statistisch-rendite':
          return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Renditen des MSCI World';
        case 'historische-garch':
          return 'Erweiterte historische Renditen mit GARCH-Volatilitätsmodellierung (Krisen-Cluster, Leverage-Effekte)';
        case 'statistisch-garch':
          return 'Statistische Renditeverteilung mit zeitvariabler GARCH-Volatilität (Mean Reversion, Fat Tails)';
        default:
          return `Normalverteilte Rendite um Parameterwert (Mittelwert: ${_steuerParameter?.renditeInvestitionstopf || 7.0}%, Volatilität: ~16%)`;
      }
    })();

    const strategies = [];
    if (simulationData.length > 0) {strategies.push('3-Topf');}
    if (oneTopfSimulationData.length > 0) {strategies.push('1-Topf');}

    // Parameters summary first
    const parameterSummary =
      `**Simulationsparameter:** ` +
      `Zeitraum: ${yearsFunded} Jahre (Alter ${retirementAge}-${berechnungBisLebensalter}), ` +
      `Startkapital: ${startingCapital.toLocaleString('de-DE')} €, ` +
      `Jährliche Entnahme: ${Math.round(annualWithdrawal).toLocaleString('de-DE')} € (${_steuerParameter?.portfolioEntnahmeRate || 3.0}%), ` +
      `Inflationsmodell: ${inflationText}, ` +
      `Renditemodell: ${investmentReturnText}, ` +
      `Tagesgeldmodell: ${(() => {
        switch (simulationOptions?.tagesgeldOption) {
          case 'keine-rendite':
            return 'Keine Rendite (0%)';
          case 'parameter-rendite':
            return `Rendite aus Parameterwert (${_steuerParameter?.zinssatzVerbrauchstopf || 1.5}%)`;
          case 'historisch-tagesgeld':
            return 'Rendite als Zufallswert aus historischen Tagesgeld-Zinssätzen';
          case 'statistisch-tagesgeld':
            return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Tagesgeld-Zinssätze';
          default:
            return `Rendite aus Parameterwert (${_steuerParameter?.zinssatzVerbrauchstopf || 1.5}%)`;
        }
      })()}, ` +
      `Zinstopfmodell: ${(() => {
        switch (simulationOptions?.zinstopfOption) {
          case 'keine-rendite':
            return 'Keine Rendite (0%)';
          case 'parameter-rendite':
            return `Rendite aus Parameterwert (${_steuerParameter?.zinssatzZinstopf || 3.0}%)`;
          case 'historisch-zinstopf':
            return 'Rendite als Zufallswert aus historischen Zinstopf-Performances';
          case 'statistisch-zinstopf':
            return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Zinstopf-Performances';
          default:
            return `Rendite aus Parameterwert (${_steuerParameter?.zinssatzZinstopf || 3.0}%)`;
        }
      })()}, ` +
      `Krisendefinition: ${(() => {
        switch (_crisisDefinition) {
          case 'negative-return':
            return 'Krise bei negativer Jahresrendite';
          case 'kapitalindex-drawdown':
            return 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung';
          default:
            return 'Krise bei negativer Jahresrendite';
        }
      })()}`;

    // Analysis of simulation results
    let analysisText = '';

    if (simulationData.length > 0 && oneTopfSimulationData.length > 0) {
      // Both strategies simulated - detailed comparison
      const threeTopfFinal = simulationData[simulationData.length - 1];
      const oneTopfFinal = oneTopfSimulationData[oneTopfSimulationData.length - 1];

      const threeTopfSuccess = threeTopfFinal.gesamtsaldo > 0;
      const oneTopfSuccess = oneTopfFinal.investitionstopfSaldo > 0;

      const threeTopfTotalEntnahme = simulationData.reduce(
        (sum, year) => sum + year.gesamtentnahme,
        0
      );
      const oneTopfTotalEntnahme = oneTopfSimulationData.reduce(
        (sum, year) => sum + year.gesamtentnahme,
        0
      );

      // Count crisis years
      const threeTopfCrisisYears = simulationData.filter(year => year.istKrise).length;
      const oneTopfCrisisYears = oneTopfSimulationData.filter(year => year.istKrise).length;

      const betterStrategy =
        threeTopfFinal.gesamtsaldo > oneTopfFinal.investitionstopfSaldo ? '3-Topf' : '1-Topf';
      const performanceDifference = Math.abs(threeTopfFinal.gesamtsaldo - oneTopfFinal.investitionstopfSaldo);

      analysisText =
        `**Ergebnisse 3-Topf:** Endkapital ${Math.round(threeTopfFinal.gesamtsaldo).toLocaleString('de-DE')} €, ` +
        `Entnahmen ${Math.round(threeTopfTotalEntnahme).toLocaleString('de-DE')} €, ` +
        `Krisenjahre ${threeTopfCrisisYears}, ` +
        `${threeTopfSuccess ? 'Erfolgreich' : 'Vorzeitig erschöpft'}\n` +
        `**Ergebnisse 1-Topf:** Endkapital ${Math.round(oneTopfFinal.investitionstopfSaldo).toLocaleString('de-DE')} €, ` +
        `Entnahmen ${Math.round(oneTopfTotalEntnahme).toLocaleString('de-DE')} €, ` +
        `Krisenjahre ${oneTopfCrisisYears}, ` +
        `${oneTopfSuccess ? 'Erfolgreich' : 'Vorzeitig erschöpft'}\n` +
        `**Vergleich:** ${betterStrategy} überlegen (+${Math.round(performanceDifference).toLocaleString('de-DE')} €). ` +
        (threeTopfSuccess && oneTopfSuccess
          ? `Beide Strategien erfolgreich über gesamten Zeitraum. `
          : threeTopfSuccess && !oneTopfSuccess
            ? `3-Topf zeigt Überlegenheit durch Krisenresilienz. `
            : !threeTopfSuccess && oneTopfSuccess
              ? `1-Topf robuster in diesem Szenario. `
              : `Beide Strategien erschöpft - Parameter anpassen. `) +
        `\n` +
        `**Fazit:** ` +
        (betterStrategy === '3-Topf'
          ? `3-Topf-Strategie zeigt Stärke durch Krisenprotektion und dynamische Topf-Aufteilung. ` +
            `Empfehlung: 3-Topf für volatile Märkte und langfristige Stabilität. `
          : `1-Topf-Strategie effizienter in diesem Szenario. ` +
            `Empfehlung: 1-Topf bei günstigen Marktbedingungen. `) +
        `Monte-Carlo-Analyse empfohlen für umfassende Risikobewertung.`;
    } else if (simulationData.length > 0) {
      // Only 3-Topf simulated
      const threeTopfFinal = simulationData[simulationData.length - 1];
      const threeTopfSuccess = threeTopfFinal.gesamtsaldo > 0;
      const threeTopfTotalEntnahme = simulationData.reduce((sum, year) => sum + year.gesamtentnahme, 0);
      const threeTopfCrisisYears = simulationData.filter(year => year.istKrise).length;

      analysisText =
        `**Ergebnisse 3-Topf:** Endkapital ${Math.round(threeTopfFinal.gesamtsaldo).toLocaleString('de-DE')} €, ` +
        `Entnahmen ${Math.round(threeTopfTotalEntnahme).toLocaleString('de-DE')} €, ` +
        `Krisenjahre ${threeTopfCrisisYears}, ` +
        `${threeTopfSuccess ? 'Erfolgreich' : 'Vorzeitig erschöpft'}\n` +
        `**Fazit:** 3-Topf-Strategie ${threeTopfSuccess ? 'erfolgreich' : 'erschöpft'}. ` +
        `Monte-Carlo-Analyse und 1-Topf-Vergleich empfohlen.`;
    } else if (oneTopfSimulationData.length > 0) {
      // Only 1-Topf simulated
      const oneTopfFinal = oneTopfSimulationData[oneTopfSimulationData.length - 1];
      const oneTopfSuccess = oneTopfFinal.investitionstopfSaldo > 0;
      const oneTopfTotalEntnahme = oneTopfSimulationData.reduce((sum, year) => sum + year.gesamtentnahme, 0);
      const oneTopfCrisisYears = oneTopfSimulationData.filter(year => year.istKrise).length;

      analysisText =
        `**Ergebnisse 1-Topf:** Endkapital ${Math.round(oneTopfFinal.investitionstopfSaldo).toLocaleString('de-DE')} €, ` +
        `Entnahmen ${Math.round(oneTopfTotalEntnahme).toLocaleString('de-DE')} €, ` +
        `Krisenjahre ${oneTopfCrisisYears}, ` +
        `${oneTopfSuccess ? 'Erfolgreich' : 'Vorzeitig erschöpft'}\n` +
        `**Fazit:** 1-Topf-Strategie ${oneTopfSuccess ? 'erfolgreich' : 'erschöpft'}. ` +
        `Monte-Carlo-Analyse und 3-Topf-Vergleich empfohlen.`;
    }

    return analysisText ? `${parameterSummary}\n\n${analysisText}` : parameterSummary;
  }, [_steuerParameter, simulationOptions, simulationData, oneTopfSimulationData, _crisisDefinition]);

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Simulation Summary - only show when simulation data is available */}
      {_steuerParameter &&
        ((simulationData && simulationData.length > 0) ||
          (oneTopfSimulationData && oneTopfSimulationData.length > 0)) && (
          <Container>
            <Header variant="h3">Simulation-Zusammenfassung</Header>
            <Box>
              <TextContent>
                <div style={{ lineHeight: '1.6', textAlign: 'justify', whiteSpace: 'pre-line' }}>
                  {simulationSummary.split('**').map((part, index) => {
                    if (index % 2 === 1) {
                      // Bold parts (every odd index is content between **)
                      return <strong key={index}>{part}</strong>;
                    } else {
                      // Regular text parts
                      return <span key={index}>{part}</span>;
                    }
                  })}
                </div>
              </TextContent>
            </Box>
          </Container>
        )}

      {/* Show message if no data */}
      {(!simulationData || simulationData.length === 0) &&
        (!oneTopfSimulationData || oneTopfSimulationData.length === 0) && (
          <Box textAlign="center" color="inherit">
            <TextContent>
              <p>Klicken Sie auf "Simulation starten", um die Ergebnisse hier anzuzeigen.</p>
            </TextContent>
          </Box>
        )}

      {/* Show results only when we have data */}
      {((simulationData && simulationData.length > 0) ||
        (oneTopfSimulationData && oneTopfSimulationData.length > 0)) && (
        <>
          {/* GARCH Information Panel (when GARCH is being used) */}
          {isAnyStrategyUsingGARCH && garchAnalysis && (
            <ExpandableSection headerText="GARCH-Volatilitätsmodellierung in Einzel-Simulation">
              <SpaceBetween direction="vertical" size="l">
                <Alert type="info" statusIconAriaLabel="GARCH Info">
                  <strong>Erweiterte Volatilitätsmodellierung aktiv:</strong> Diese Einzel-Simulation verwendet EGARCH(1,1) Modelle mit True Maximum Likelihood Estimation für realistische Volatilitätsmuster, Krisen-Clustering und Leverage-Effekte. Die v2.4.2 Implementierung bietet verbesserte Parameterschätzung und Stabilitätskorrekturen.
                </Alert>

                {/* Model Overview Section */}
                <Container header={<Header variant="h3">Modell-Übersicht</Header>}>
                  <ColumnLayout columns={3} borders="horizontal">
                    <Box>
                      <Box variant="awsui-key-label">GARCH-Modell</Box>
                      <Box variant="awsui-value-large">EGARCH(1,1)</Box>
                      <Box variant="small" color="text-body-secondary">
                        Exponentieller GARCH mit asymmetrischen Effekten
                      </Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Datenbasis</Box>
                      <Box variant="awsui-value-large">{garchAnalysis.dataPoints}</Box>
                      <Box variant="small" color="text-body-secondary">
                        Historische Datenpunkte (MSCI World)
                      </Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Anwendungsbereich</Box>
                      <Box variant="awsui-value-large">Investitionstopf</Box>
                      <Box variant="small" color="text-body-secondary">
                        Aktienrenditen mit Volatilitäts-Clustering
                      </Box>
                    </Box>
                  </ColumnLayout>
                </Container>

                {/* Mathematical Specification */}
                <Container header={<Header variant="h3">Mathematische Spezifikation</Header>}>
                  <SpaceBetween direction="vertical" size="s">
                    <Box>
                      <Box variant="awsui-key-label">EGARCH(1,1) Formel nach Nelson (1991)</Box>
                      <TextContent>
                        <p><strong>log(σₜ²) = ω + β·log(σₜ₋₁²) + α·g(εₜ₋₁)</strong></p>
                        <p>wobei: <strong>g(ε) = θ·ε + γ·(|ε| - E[|ε|])</strong></p>
                        <p>und: <strong>εₜ = (rₜ - μ) / σₜ</strong> (standardisierte Residuen)</p>
                      </TextContent>
                    </Box>
                    <ColumnLayout columns={2} borders="horizontal">
                      <Box>
                        <Box variant="awsui-key-label">Innovations-Verteilung</Box>
                        <Box variant="awsui-value-large">
                          {parseFloat(garchAnalysis.kurtosis) > 5 ? 'Student-t' : 'Normal'}
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          {parseFloat(garchAnalysis.kurtosis) > 5 
                            ? `Heavy Tails (Kurtosis: ${garchAnalysis.kurtosis})`
                            : `Standard Normal (Kurtosis: ${garchAnalysis.kurtosis})`
                          }
                        </Box>
                      </Box>
                      <Box>
                        <Box variant="awsui-key-label">E[|ε|] Korrektur</Box>
                        <Box variant="awsui-value-large">
                          {parseFloat(garchAnalysis.kurtosis) > 5 ? '√(2(ν-2)/πν)' : '√(2/π)'}
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Erwartungswert der absoluten standardisierten Residuen
                        </Box>
                      </Box>
                    </ColumnLayout>
                  </SpaceBetween>
                </Container>

                {/* Historical Data Statistics */}
                <Container header={<Header variant="h3">Historische Datenstatistiken</Header>}>
                  <ColumnLayout columns={4} borders="horizontal">
                    <Box>
                      <Box variant="awsui-key-label">Durchschnittsrendite (μ)</Box>
                      <Box variant="awsui-value-large">{garchAnalysis.historicalMean}%</Box>
                      <Box variant="small" color="text-body-secondary">
                        Historischer Mittelwert p.a.
                      </Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Volatilität (σ)</Box>
                      <Box variant="awsui-value-large">{garchAnalysis.historicalVolatility}%</Box>
                      <Box variant="small" color="text-body-secondary">
                        Historische Standardabweichung p.a.
                      </Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Schiefe (Skewness)</Box>
                      <Box variant="awsui-value-large">{garchAnalysis.skewness}</Box>
                      <Box variant="small" color="text-body-secondary">
                        {parseFloat(garchAnalysis.skewness) < -0.1 ? 'Links-schief (Crash-Risiko)' :
                         parseFloat(garchAnalysis.skewness) > 0.1 ? 'Rechts-schief' : 'Symmetrisch'}
                      </Box>
                    </Box>
                    <Box>
                      <Box variant="awsui-key-label">Kurtosis (Wölbung)</Box>
                      <Box variant="awsui-value-large">{garchAnalysis.kurtosis}</Box>
                      <Box variant="small" color="text-body-secondary">
                        {parseFloat(garchAnalysis.kurtosis) > 3 ? 'Leptokurtisch (Fat Tails)' : 'Mesokurtisch'}
                      </Box>
                    </Box>
                  </ColumnLayout>
                </Container>

                {/* Technical Parameters Section */}
                <Container header={<Header variant="h3">EGARCH Parameter-Schätzungen</Header>}>
                  <ColumnLayout columns={3} borders="horizontal">
                    <Box>
                      <Box variant="h3">Grundparameter</Box>
                      <SpaceBetween direction="vertical" size="s">
                        <Box>
                          <Box variant="awsui-key-label">Konstante (ω)</Box>
                          <Box variant="awsui-value-large">
                            {/* Omega needs to be added to the analysis */}
                            Geschätzt
                          </Box>
                          <Box variant="small" color="text-body-secondary">
                            Langfristige log-Varianz Komponente
                          </Box>
                        </Box>
                        <Box>
                          <Box variant="awsui-key-label">Volatilitäts-Persistenz (β)</Box>
                          <Box variant="awsui-value-large">
                            {garchAnalysis.garchParameters.volatilityPersistence}
                          </Box>
                          <Box variant="small" color="text-body-secondary">
                            Langfristige Volatilitäts-Erhaltung
                          </Box>
                        </Box>
                      </SpaceBetween>
                    </Box>
                    <Box>
                      <Box variant="h3">ARCH/GARCH Effekte</Box>
                      <SpaceBetween direction="vertical" size="s">
                        <Box>
                          <Box variant="awsui-key-label">ARCH-Effekt (α)</Box>
                          <Box variant="awsui-value-large">
                            {garchAnalysis.garchParameters.archEffect}
                          </Box>
                          <Box variant="small" color="text-body-secondary">
                            Volatilitäts-Clustering Stärke
                          </Box>
                        </Box>
                        <Box>
                          <Box variant="awsui-key-label">Clustering-Effekt (γ)</Box>
                          <Box variant="awsui-value-large">
                            {garchAnalysis.garchParameters.clusteringEffect}
                          </Box>
                          <Box variant="small" color="text-body-secondary">
                            Magnituden-Effekt: große Schocks erhöhen Folge-Volatilität
                          </Box>
                        </Box>
                      </SpaceBetween>
                    </Box>
                    <Box>
                      <Box variant="h3">Asymmetrie</Box>
                      <SpaceBetween direction="vertical" size="s">
                        <Box>
                          <Box variant="awsui-key-label">Leverage-Effekt (θ)</Box>
                          <Box variant="awsui-value-large">
                            {garchAnalysis.garchParameters.leverageEffect}
                          </Box>
                          <Box variant="small" color="text-body-secondary">
                            Negative Schocks verstärken Volatilität
                          </Box>
                        </Box>
                        <Box>
                          <Box variant="awsui-key-label">Asymmetrie-Interpretation</Box>
                          <Badge color={parseFloat(garchAnalysis.garchParameters.leverageEffect) < 0 ? "green" : "severity-medium"}>
                            {parseFloat(garchAnalysis.garchParameters.leverageEffect) < 0 ? "Leverage-Effekt bestätigt" : "Asymmetrie schwach"}
                          </Badge>
                          <Box variant="small" color="text-body-secondary">
                            θ &lt; 0: Bad News erhöht Volatilität mehr
                          </Box>
                        </Box>
                      </SpaceBetween>
                    </Box>
                  </ColumnLayout>
                </Container>

                {/* Estimation Quality Section */}
                {garchAnalysis.estimationQuality && (
                  <Container header={<Header variant="h3">Modell-Qualität & Schätzung</Header>}>
                    <ColumnLayout columns={3} borders="horizontal">
                      <Box>
                        <Box variant="awsui-key-label">Schätzungsmethode</Box>
                        <Box variant="awsui-value-large">{garchAnalysis.estimationQuality.method}</Box>
                        <Box variant="small" color="text-body-secondary">
                          {garchAnalysis.estimationQuality.description}
                        </Box>
                      </Box>
                      <Box>
                        <Box variant="awsui-key-label">Modell-Qualität</Box>
                        <Badge color={garchAnalysis.estimationQuality.isFallback ? "severity-medium" : "green"}>
                          {garchAnalysis.estimationQuality.quality}
                        </Badge>
                        <Box variant="small" color="text-body-secondary">
                          {garchAnalysis.estimationQuality.isFallback ? 'Heuristisch stabil' : 'MLE-optimiert'}
                        </Box>
                      </Box>
                      <Box>
                        <Box variant="awsui-key-label">Modell-Güte</Box>
                        <Box variant="awsui-value-large">
                          {garchAnalysis.estimationQuality.logLikelihood || 'N/A'}
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          {garchAnalysis.estimationQuality.iterations ? 
                            `Log-Likelihood (${garchAnalysis.estimationQuality.iterations} Iterationen)` : 
                            'Log-Likelihood Wert'}
                        </Box>
                      </Box>
                    </ColumnLayout>
                  </Container>
                )}

                {/* Strategy-specific GARCH statistics */}
                {(isThreeTopfUsingGARCH || isOneTopfUsingGARCH) && (
                  <ColumnLayout columns={2} borders="vertical">
                    {isThreeTopfUsingGARCH && (
                      <Box>
                        <Header variant="h3">3-Topf GARCH-Analyse</Header>
                        <SpaceBetween direction="vertical" size="xs">
                          <Box>
                            <Box variant="awsui-key-label">Simulationsmodell</Box>
                            <Box variant="awsui-value-large">EGARCH(1,1)</Box>
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">Datenbasis</Box>
                            <Badge color="blue">
                              {garchAnalysis.dataPoints} historische Daten
                            </Badge>
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">Volatilitäts-Modellierung</Box>
                            <Badge color="green">
                              Krisen-Cluster & Leverage
                            </Badge>
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">Renditeverteilung</Box>
                            <Badge color="severity-medium">
                              Nicht-normal mit Fat Tails
                            </Badge>
                          </Box>
                        </SpaceBetween>
                      </Box>
                    )}

                    {isOneTopfUsingGARCH && (
                      <Box>
                        <Header variant="h3">1-Topf GARCH-Analyse</Header>
                        <SpaceBetween direction="vertical" size="xs">
                          <Box>
                            <Box variant="awsui-key-label">Simulationsmodell</Box>
                            <Box variant="awsui-value-large">EGARCH(1,1)</Box>
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">Datenbasis</Box>
                            <Badge color="blue">
                              {garchAnalysis.dataPoints} historische Daten
                            </Badge>
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">Volatilitäts-Modellierung</Box>
                            <Badge color="green">
                              Krisen-Cluster & Leverage
                            </Badge>
                          </Box>
                          <Box>
                            <Box variant="awsui-key-label">Renditeverteilung</Box>
                            <Badge color="severity-medium">
                              Nicht-normal mit Fat Tails
                            </Badge>
                          </Box>
                        </SpaceBetween>
                      </Box>
                    )}
                  </ColumnLayout>
                )}
              </SpaceBetween>
            </ExpandableSection>
          )}

          {/* Combined Charts Section - Side by Side Layout */}
          <ColumnLayout columns={2} variant="text-grid">
            {/* Portfolio Development Chart */}
            <Container
              header={
                <Header variant="h3">Portfolio-Entwicklung (3-Topf vs. 1-Topf)</Header>
              }
            >
              {portfolioChartSeries.length > 0 && (
                <LineChart
                  series={portfolioChartSeries}
                  xDomain={
                    _steuerParameter
                      ? [_steuerParameter.retirementAge, _steuerParameter.berechnungBisLebensalter]
                      : [58, 95]
                  }
                  yTitle="Portfolio-Wert (€)"
                  xTitle="Alter"
                  height={400}
                  hideFilter={true}
                  hideLegend={false}
                  statusType="finished"
                  emphasizeBaselineAxis={false}
                  xScaleType="linear"
                  detailPopoverSeriesContent={({ series, x, y }) => {
                    const age = Number(x);
                    const isCrisisAtStart = crisisYearsAtStart.includes(age);
                    
                    return {
                      key: series.title,
                      value: `${Math.round(y)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €${isCrisisAtStart ? ' (01.01 in Krise)' : ''}`,
                    };
                  }}
                  ariaLabel="Portfolio-Entwicklung Vergleich"
                />
              )}
            </Container>

            {/* Kapitalindex Chart */}
            <Container
              header={
                <Header variant="h3">Kapitalindex-Entwicklung (3-Topf vs. 1-Topf)</Header>
              }
            >
              {kapitalindexChartSeries.length > 0 && (
                <LineChart
                  series={kapitalindexChartSeries}
                  xDomain={
                    _steuerParameter
                      ? [_steuerParameter.retirementAge, _steuerParameter.berechnungBisLebensalter]
                      : [58, 95]
                  }
                  yTitle="Kapitalindex"
                  xTitle="Alter"
                  height={400}
                  hideFilter={true}
                  hideLegend={false}
                  statusType="finished"
                  emphasizeBaselineAxis={false}
                  xScaleType="linear"
                  detailPopoverSeriesContent={({ series, x, y }) => {
                    const age = Number(x);
                    const isCrisisAtStart = crisisYearsAtStart.includes(age);
                    
                    return {
                      key: series.title,
                      value: `${Math.round(y)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}${isCrisisAtStart ? ' (01.01 in Krise)' : ''}`,
                    };
                  }}
                  ariaLabel="Kapitalindex-Entwicklung Vergleich"
                />
              )}
            </Container>
          </ColumnLayout>

          {/* Combined KPI Statistics */}
          <ColumnLayout columns={4} variant="text-grid">
            <Container>
              <Header variant="h3">3-Topf Portfolio</Header>
              <SpaceBetween direction="vertical" size="xs">
                <div>
                  <strong>Simulationsdauer:</strong>
                  <br />
                  {combinedKPIs.threeTopf.hasData
                    ? `${combinedKPIs.threeTopf.totalYears} Jahre (${combinedKPIs.threeTopf.crisisYears} Krisenjahre)`
                    : 'Keine Daten'}
                </div>
                <div>
                  <strong>Endguthaben:</strong>
                  <br />
                  {combinedKPIs.threeTopf.hasData
                    ? `${Math.round(combinedKPIs.threeTopf.finalBalance).toLocaleString()} €`
                    : 'Keine Daten'}
                </div>
                <div>
                  <strong>Status:</strong>
                  <br />
                  {combinedKPIs.threeTopf.hasData ? (
                    <Badge color={combinedKPIs.threeTopf.successful ? 'green' : 'red'}>
                      {combinedKPIs.threeTopf.successful ? 'Erfolgreich' : 'Kapital erschöpft'}
                    </Badge>
                  ) : (
                    'Keine Daten'
                  )}
                </div>
              </SpaceBetween>
            </Container>

            <Container>
              <Header variant="h3">1-Topf Portfolio</Header>
              <SpaceBetween direction="vertical" size="xs">
                <div>
                  <strong>Simulationsdauer:</strong>
                  <br />
                  {combinedKPIs.oneTopf.hasData
                    ? `${combinedKPIs.oneTopf.totalYears} Jahre (${combinedKPIs.oneTopf.crisisYears} Krisenjahre)`
                    : 'Keine Daten'}
                </div>
                <div>
                  <strong>Endguthaben:</strong>
                  <br />
                  {combinedKPIs.oneTopf.hasData
                    ? `${Math.round(combinedKPIs.oneTopf.finalBalance).toLocaleString()} €`
                    : 'Keine Daten'}
                </div>
                <div>
                  <strong>Status:</strong>
                  <br />
                  {combinedKPIs.oneTopf.hasData ? (
                    <Badge color={combinedKPIs.oneTopf.successful ? 'green' : 'red'}>
                      {combinedKPIs.oneTopf.successful ? 'Erfolgreich' : 'Kapital erschöpft'}
                    </Badge>
                  ) : (
                    'Keine Daten'
                  )}
                </div>
              </SpaceBetween>
            </Container>

            <Container>
              <Header variant="h3">Vergleich</Header>
              <SpaceBetween direction="vertical" size="xs">
                <div>
                  <strong>Beste Laufzeit:</strong>
                  <br />
                  {(() => {
                    const strategies = [];
                    if (combinedKPIs.threeTopf.hasData)
                      {strategies.push({ name: '3-Topf', years: combinedKPIs.threeTopf.totalYears });}

                    if (combinedKPIs.oneTopf.hasData)
                      {strategies.push({ name: '1-Topf', years: combinedKPIs.oneTopf.totalYears });}

                    if (strategies.length === 0) {return 'Nicht verfügbar';}

                    const maxYears = Math.max(...strategies.map(s => s.years));
                    const winners = strategies.filter(s => s.years === maxYears);
                    return winners.map(w => w.name).join(', ');
                  })()}
                </div>
                <div>
                  <strong>Höchstes Endguthaben:</strong>
                  <br />
                  {(() => {
                    const strategies = [];
                    if (combinedKPIs.threeTopf.hasData)
                      {strategies.push({
                        name: '3-Topf',
                        balance: combinedKPIs.threeTopf.finalBalance,
                      });}

                    if (combinedKPIs.oneTopf.hasData)
                      {strategies.push({
                        name: '1-Topf',
                        balance: combinedKPIs.oneTopf.finalBalance,
                      });}

                    if (strategies.length === 0) {return 'Nicht verfügbar';}

                    const maxBalance = Math.max(...strategies.map(s => s.balance));
                    const winners = strategies.filter(s => s.balance === maxBalance);
                    return winners.map(w => w.name).join(', ');
                  })()}
                </div>
                <div>
                  <strong>Erfolgreichste Strategien:</strong>
                  <br />
                  {(() => {
                    const successful = [];
                    if (combinedKPIs.threeTopf.hasData && combinedKPIs.threeTopf.successful)
                      {successful.push('3-Topf');}

                    if (combinedKPIs.oneTopf.hasData && combinedKPIs.oneTopf.successful)
                      {successful.push('1-Topf');}

                    return successful.length > 0 ? successful.join(', ') : 'Alle erschöpft';
                  })()}
                </div>
              </SpaceBetween>
            </Container>
            
            <Container>
              {/* Empty 4th column for layout balance */}
            </Container>
          </ColumnLayout>

          {/* Stacked Detail Tables */}
          <SpaceBetween direction="vertical" size="l">
            {/* 3-Topf Table (First) */}
            <Container>
              <Header variant="h3" description="Detaillierte Ergebnisse der 3-Topf Strategie">
                3-Topf Simulation Details
              </Header>
              {combinedKPIs.threeTopf.hasData ? (
                  <div className="top-aligned-table">
                    <Table
                      items={simulationData.filter(row =>
                      _steuerParameter ? row.alter >= _steuerParameter.retirementAge : row.jahr >= 0
                      )}
                      columnDefinitions={threeTopfTableColumns}
                      variant="embedded"
                      trackBy="jahr"
                    empty={
                      <Box textAlign="center" color="inherit">
                        <TextContent>
                          <p>Keine 3-Topf Simulationsdaten verfügbar.</p>
                        </TextContent>
                      </Box>
                    }
                  />
                  </div>
              ) : (
                <Box textAlign="center" color="inherit">
                  <TextContent>
                    <p>Führen Sie eine 3-Topf Simulation aus, um die Details hier anzuzeigen.</p>
                  </TextContent>
                </Box>
              )}
            </Container>

            {/* 1-Topf Table (Third) */}
            <Container>
              <Header variant="h3" description="Detaillierte Ergebnisse der 1-Topf Strategie">
                1-Topf Simulation Details
              </Header>
              {combinedKPIs.oneTopf.hasData ? (
                <div className="top-aligned-table">
                  <Table
                    items={oneTopfSimulationData.filter(row =>
                      _steuerParameter ? row.alter >= _steuerParameter.retirementAge : row.jahr >= 0
                    )}
                    columnDefinitions={oneTopfTableColumns}
                    variant="embedded"
                    trackBy="jahr"
                  empty={
                    <Box textAlign="center" color="inherit">
                      <TextContent>
                        <p>Keine 1-Topf Simulationsdaten verfügbar.</p>
                      </TextContent>
                    </Box>
                  }
                />
                </div>
              ) : (
                <Box textAlign="center" color="inherit">
                  <TextContent>
                    <p>Führen Sie eine 1-Topf Simulation aus, um die Details hier anzuzeigen.</p>
                  </TextContent>
                </Box>
              )}
            </Container>
          </SpaceBetween>
        </>
      )}
    </SpaceBetween>
  );
};